import type { Message } from '../common'

import { useInfiniteQuery } from '@tanstack/react-query'

import http from '../../../lib/http'

export type MessagesQueryKey = [
  'chats/messages',
  {
    userId?: string
    chatId?: string
  }
]

export type ChatMessagesPage = Message[]

export type UseChatMessagesQueryOptions = MessagesQueryKey[1]


export function useChatMessagesQuery ({ userId, chatId }: UseChatMessagesQueryOptions) {
  let pageSize = 10

  return useInfiniteQuery({
    queryKey: getChatMessagesQueryKey({ userId, chatId }),

    async queryFn ({ queryKey, pageParam, signal }) {
      let [ , { userId, chatId }] = queryKey

      if (
        typeof userId !== 'string' ||
        !userId ||
        typeof chatId !== 'string' ||
        !chatId
      ) {
        throw new Error('User Id or Chat Id is undefined')
      }

      if (!pageParam.fetch) {
        return []
      }

      const offset = !pageParam.initial && !isNaN(pageParam.date.getTime()) ? [[ 'offset', pageParam.date.toISOString() ]] : []

      let params = new URLSearchParams([
        [ 'limit', pageSize.toString() ],
        ...offset
      ])

      let response = await http.getRequest(`/chats/${chatId}/messages?${params.toString()}`, {}, { signal })
      return response as ChatMessagesPage
    },

    initialPageParam: {
      date: new Date(),
      fetch: true,
      initial: true
    },

    getNextPageParam (lastPage, allPages) {
      if (!allPages.length) {
        return {
          date: new Date(),
          fetch: true,
          initial: false
        }
      }

      let createdAt = lastPage?.[lastPage.length - 1]?.created_at
      let date = new Date(createdAt ?? '')

      if (isNaN(date.getTime())) {
        return {
          date: new Date(),
          fetch: false,
          initial: false
        }
      }

      if (lastPage?.length < pageSize) {
        return {
          date: new Date(),
          fetch: false,
          initial: false
        }
      }

      return {
        date,
        fetch: true,
        initial: false
      }
    },

    enabled:
      typeof userId === 'string' &&
      userId.length > 0 &&
      typeof chatId === 'string' &&
      chatId.length > 0
  })
}

export function getChatMessagesQueryKey (options: UseChatMessagesQueryOptions): MessagesQueryKey {
  return [
    'chats/messages',
    { ...options }
  ]
}
